/**
 * FrontDesk Email Templates API
 * Paths use /api/accounts/email-templates (JWT scopes print house).
 */

import { BaseChatwootService } from "../core/base-chatwoot-service";
import type {
  CreateEmailTemplatePayload,
  EmailTemplate,
  EmailTemplateDetailResponse,
  EmailTemplateListResponse,
  UpdateEmailTemplatePayload,
} from "../types/email-templates.types";

function unwrapList(response: any): EmailTemplate[] {
  if (Array.isArray(response?.data?.payload)) return response.data.payload;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response)) return response as EmailTemplate[];
  return [];
}

function unwrapOne(response: any): EmailTemplate {
  if (response?.data?.payload) return response.data.payload;
  if (response?.payload) return response.payload;
  return response as EmailTemplate;
}

export class ChatwootEmailTemplatesService extends BaseChatwootService {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  async listTemplates(options?: {
    activeOnly?: boolean;
  }): Promise<EmailTemplate[]> {
    const qs =
      options?.activeOnly === true ? "?active_only=true" : "";
    const response = await this.get<EmailTemplateListResponse>(
      `/api/accounts/email-templates${qs}`
    );
    return unwrapList(response);
  }

  async getTemplate(templateId: number | string): Promise<EmailTemplate> {
    const response = await this.get<EmailTemplateDetailResponse>(
      `/api/accounts/email-templates/${templateId}`
    );
    return unwrapOne(response);
  }

  async createTemplate(
    payload: CreateEmailTemplatePayload
  ): Promise<EmailTemplate> {
    const response = await this.post<EmailTemplateDetailResponse>(
      `/api/accounts/email-templates`,
      { template: payload }
    );
    return unwrapOne(response);
  }

  async updateTemplate(
    templateId: number | string,
    payload: UpdateEmailTemplatePayload
  ): Promise<EmailTemplate> {
    const response = await this.put<EmailTemplateDetailResponse>(
      `/api/accounts/email-templates/${templateId}`,
      { template: payload }
    );
    return unwrapOne(response);
  }

  async duplicateTemplate(
    templateId: number | string
  ): Promise<EmailTemplate> {
    const response = await this.post<EmailTemplateDetailResponse>(
      `/api/accounts/email-templates/${templateId}/duplicate`,
      {}
    );
    return unwrapOne(response);
  }

  async deleteTemplate(templateId: number | string): Promise<void> {
    await this.delete(`/api/accounts/email-templates/${templateId}`);
  }
}
